import CarouselSelector from 'components/carousel-selector';
import usePreventHorizontalScroll from 'hooks/slider/use-prevent-horizontal-scroll';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import React, { memo, useMemo, useRef, useState } from 'react';
import SlickSlider, { Settings } from 'react-slick';
import { gtm, scrollElement } from 'tracking';
import MainHeadline from '../../../../components/typography/main-headline';
import {
    ISectionIngredients,
    ISectionsliderFields,
    ISlide,
    ISlideRedesign,
} from '../../../../global';
import Ingredients from '../ingredients';
import ReadableSlide from './readable-slide';
import SlideNewDesign from './slide-new-design';
import styles from './slider.module.css';

const TRACKING_NAME = 'Promo Header';

const getInitialSlide = (slides: ISlide[], query: ParsedUrlQuery) => {
  if (query.promotionId) {
    const slide = slides.findIndex((s) => s.fields.id === query.promotionId);
    if (slide !== -1) {
      return slide;
    }
  }
  return 0;
};

type SliderProps = {
  priority?: boolean;
  /** if true slider design is override  */
  overrideSliderDesign?: boolean;
} & ISectionsliderFields;

/**
 * Slider component
 * overrideSliderDesign is used to isolate the design for home page
 * @SEE : https://petsdeli.atlassian.net/browse/PT-4789
 */
const Slider: React.FC<SliderProps> = ({
  slides,
  readableSlider,
  autoplay,
  priority,
  nutritionGuidance,
  overrideSliderDesign = false,
}) => {
  const router = useRouter();
  const sliderRef = useRef<SlickSlider>(null);

  usePreventHorizontalScroll(sliderRef);

  const trackSlideScroll = (): Promise<void> =>
    gtm(
      scrollElement({
        element: [TRACKING_NAME],
      })
    );

  const [currentIndex, setCurrentIndex] = useState(0);
  const settings: Settings = useMemo(() => {
    return {
      arrows: !readableSlider,
      dots: readableSlider,
      autoplay: autoplay && autoplay === true ? true : false,
      autoplaySpeed: 8000,
      adaptiveHeight: true,
      initialSlide: getInitialSlide(slides as ISlide[], router.query),
      className: styles.slider,
      appendDots: (dots) => <ul>{dots}</ul>,
      afterChange: (index) => setCurrentIndex(index),
      onSwipe: () => {
        const hasRedesignSlide = slides?.some(
          (slide) => slide.sys.contentType.sys.id === 'slideRedesign'
        );
        if (hasRedesignSlide) trackSlideScroll();
      },
      ...(overrideSliderDesign
        ? {
            arrows: false,
            dots: false,
            adaptiveHeight: false,
          }
        : {}),
    };
  }, [autoplay, overrideSliderDesign, readableSlider, router.query, slides]);

  if (!slides || slides.length === 0) {
    return null;
  }

  const slideContent = (slide: ISlide) => {
    if (!slide.fields.title && !slide.fields.subTitle) {
      return <div />;
    }

    return (
      <div className="text-center">
        <MainHeadline color="text-text-dark-bg-contrast-white">
          <h3>{slide.fields.title}</h3>
        </MainHeadline>
        <h2 className="pd-headline h6">{slide.fields.subTitle}</h2>
      </div>
    );
  };

  if (slides.length === 1 && readableSlider) {
    return (
      <ReadableSlide
        slide={slides[0] as ISlide}
        nutritionGuidanceEnabled={nutritionGuidance}
        priority={priority}
      />
    );
  }

  return (
    <>
      <SlickSlider {...settings} ref={sliderRef}>
        {slides.map((slide) => {
          if (slide.sys.contentType.sys.id === 'sectionIngredients') {
            const currentSlide = slide as ISectionIngredients;
            return (
              <Ingredients key={currentSlide.sys.id} {...currentSlide.fields} />
            );
          } else if (slide.sys.contentType.sys.id === 'slideRedesign') {
            const currentSlide = slide as ISlideRedesign;
            return (
              <SlideNewDesign
                trackingName={TRACKING_NAME}
                {...currentSlide.fields}
                key={currentSlide.sys.id}
                priority={priority}
              />
            );
          } else {
            const currentSlide = slide as ISlide;

            if (readableSlider) {
              return (
                <ReadableSlide
                  priority={priority}
                  key={currentSlide.sys.id}
                  slide={currentSlide}
                />
              );
            }

            const backgroundImage =
              currentSlide.fields.testBackgroundImage ||
              currentSlide.fields.backgroundImage;

            return (
              <div key={currentSlide.sys.id}>
                {currentSlide.fields.link && currentSlide.fields.link ? (
                  <a
                    href={currentSlide.fields.link}
                    className="container flex items-center justify-center bg-cover bg-center bg-no-repeat"
                    style={{
                      minHeight: '275px',
                      backgroundImage: `url(${
                        backgroundImage ? backgroundImage.fields.file.url : ''
                      })`,
                    }}
                  >
                    {slideContent(currentSlide)}
                  </a>
                ) : (
                  <div
                    className="container flex items-center justify-center bg-cover bg-center bg-no-repeat"
                    style={{
                      minHeight: '275px',
                      backgroundImage: `url(${
                        backgroundImage ? backgroundImage.fields.file.url : ''
                      })`,
                    }}
                  >
                    {slideContent(currentSlide)}
                  </div>
                )}
              </div>
            );
          }
        })}
      </SlickSlider>
      <CarouselSelector
        className="my-3"
        onClick={(index) => {
          sliderRef.current?.slickGoTo(index);
          trackSlideScroll();
        }}
        selectedSlideIndex={currentIndex}
        slidesLength={slides.length}
      />
    </>
  );
};

export default memo(Slider);
